import { useEffect, useRef, useState, useCallback } from "react";
import { CircularProgress, Box, Button, Typography } from "@mui/material";

const SCRIPT_ID = "termly-jssdk";
const MAX_USER_RETRIES = 3;
const AUTO_RETRIES = 2;
const LOAD_DEFER_MS = 100;
const CONTENT_TIMEOUT_MS = 15000;

const TermlyEmbed = ({ policyId, label }) => {
  const embedRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [userRetryCount, setUserRetryCount] = useState(0);
  const activeRef = useRef(true);
  const observerRef = useRef(null);
  const timeoutRef = useRef(null);
  const timerRef = useRef(null);
  const autoRetryRef = useRef(0);

  const stopWaiting = useCallback(() => {
    observerRef.current?.disconnect();
    clearTimeout(timeoutRef.current);
  }, []);

  // Forward-declared via ref so loadTermly can call it without a dep cycle
  const loadTermlyRef = useRef(null);

  const waitForContent = useCallback(() => {
    stopWaiting();

    observerRef.current = new MutationObserver(() => {
      if (!activeRef.current) return;
      if (embedRef.current?.children.length > 0) {
        stopWaiting();
        setStatus("success");
      }
    });

    if (embedRef.current) {
      observerRef.current.observe(embedRef.current, { childList: true, subtree: true });
    }

    timeoutRef.current = setTimeout(() => {
      if (!activeRef.current) return;
      stopWaiting();
      if (autoRetryRef.current < AUTO_RETRIES) {
        autoRetryRef.current += 1;
        loadTermlyRef.current?.();
      } else {
        setStatus("error");
      }
    }, CONTENT_TIMEOUT_MS);
  }, [stopWaiting]);

  const loadTermly = useCallback(() => {
    stopWaiting();
    clearTimeout(timerRef.current);
    activeRef.current = true;
    setStatus("loading");

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) existing.remove();

    if (embedRef.current) embedRef.current.innerHTML = "";

    timerRef.current = setTimeout(() => {
      if (!activeRef.current) return;

      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      // Cache-bust forces the browser to re-execute the script on every mount.
      // Without this, Termly's initialization guard (if (window.Termly) return)
      // skips re-scanning the DOM when navigating back to the page in a SPA.
      script.src = `https://app.termly.io/embed-policy.min.js?_=${Date.now()}`;
      script.async = true;
      script.onload = () => {
        if (!activeRef.current) return;
        // Termly may inject synchronously during script execution, before onload
        if (embedRef.current?.children.length > 0) {
          setStatus("success");
        } else {
          waitForContent();
        }
      };
      script.onerror = () => {
        if (!activeRef.current) return;
        if (autoRetryRef.current < AUTO_RETRIES) {
          autoRetryRef.current += 1;
          loadTermlyRef.current?.();
        } else {
          setStatus("error");
        }
      };

      document.body.appendChild(script);
    }, LOAD_DEFER_MS);
  }, [stopWaiting, waitForContent]);

  loadTermlyRef.current = loadTermly;

  useEffect(() => {
    window.scrollTo(0, 0);
    activeRef.current = true;
    autoRetryRef.current = 0;
    loadTermly();
    return () => {
      activeRef.current = false;
      stopWaiting();
      clearTimeout(timerRef.current);
    };
  }, [loadTermly, stopWaiting]);

  const handleUserRetry = () => {
    if (userRetryCount < MAX_USER_RETRIES) {
      setUserRetryCount((c) => c + 1);
      autoRetryRef.current = 0;
      loadTermly();
    }
  };

  return (
    <div style={{ padding: "40px", maxWidth: "960px", margin: "0 auto" }}>
      {status === "loading" && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <CircularProgress />
        </Box>
      )}
      {status === "error" && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, minHeight: "400px", justifyContent: "center" }}>
          <Typography variant="body1" color="text.secondary">
            Failed to load the {label}.
          </Typography>
          {userRetryCount < MAX_USER_RETRIES ? (
            <Button variant="outlined" onClick={handleUserRetry}>
              Retry ({MAX_USER_RETRIES - userRetryCount} attempts left)
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Please refresh the page or try again later.
            </Typography>
          )}
        </Box>
      )}
      <div
        ref={embedRef}
        name="termly-embed"
        data-id={policyId}
        data-type="iframe"
        style={{ display: status === "success" ? "block" : "none" }}
      />
    </div>
  );
};

export default TermlyEmbed;
